import showFREDialogCallout from './FREDialogCallout';
import shouldShowSpeedyMeetingsTeachingUI from '../utils/shouldShowSpeedyMeetingsTeachingUI';
import { ButtonType, IButtonProps } from '@fluentui/react/lib/Button';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { lightable, lighted, LightningId, LightningProps } from 'owa-lightning-v2';
import loc from 'owa-localize';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import * as React from 'react';
import {
    speedyMeetingsBubbleHeader,
    speedyMeetingsBubbleBodyText,
    speedyMeetingsBubblePrimaryButtonText,
    speedyMeetingsBubbleSecondaryButtonText,
} from './SpeedyMeetingsTenantTeachingBubble.locstring.json';

export interface SpeedyMeetingsTenantTeachingBubbleProps extends LightningProps {
    target: HTMLElement | React.RefObject<Element>;
}

export const SpeedyMeetingsTenantTeachingBubble = lightable(
    observer(function SpeedyMeetingsTenantTeachingBubble(
        props: SpeedyMeetingsTenantTeachingBubbleProps
    ) {
        const onClose = React.useCallback(() => {
            logUsage(
                'SpeedyMeetingsTenantTeachingBubble',
                { isBubbleClosed: true },
                { isCore: true }
            );
            lighted(LightningId.SpeedyMeetingsTenant);
        }, []);

        const onViewSettings = React.useCallback(() => {
            logUsage(
                'SpeedyMeetingsTenantTeachingBubble',
                { isViewSettingsClicked: true },
                { isCore: true }
            );
            lazyMountAndShowFullOptions.importAndExecute(
                'calendar',
                'eventAndInvitations',
                'event'
            );
            showFREDialogCallout(() => document.getElementById('shortenEventsOption'));
            onClose();
        }, []);

        const gotItButtonProps: IButtonProps = {
            buttonType: ButtonType.primary,
            children: loc(speedyMeetingsBubblePrimaryButtonText),
            onClick: onClose,
        };

        const viewSettingsButtonProps: IButtonProps = {
            buttonType: ButtonType.primary,
            children: loc(speedyMeetingsBubbleSecondaryButtonText),
            onClick: onViewSettings,
        };

        return (
            <TeachingBubble
                calloutProps={{
                    beakWidth: 15,
                    preventDismissOnScroll: true,
                    preventDismissOnLostFocus: true,
                    directionalHint: DirectionalHint.bottomCenter,
                }}
                target={props.target}
                isWide={false}
                onDismiss={onClose}
                headline={loc(speedyMeetingsBubbleHeader)}
                hasSmallHeadline={false}
                primaryButtonProps={gotItButtonProps}
                secondaryButtonProps={viewSettingsButtonProps}>
                {<span>{loc(speedyMeetingsBubbleBodyText)}</span>}
            </TeachingBubble>
        );
    })
);

export default function renderSpeedyMeetingsTeachingUI(
    target: HTMLElement | React.RefObject<Element>,
    shouldAdjustStartTimeEndTime: boolean | undefined
): JSX.Element | null {
    if (!shouldShowSpeedyMeetingsTeachingUI(shouldAdjustStartTimeEndTime)) {
        return null;
    }

    return (
        <SpeedyMeetingsTenantTeachingBubble
            target={target}
            lid={LightningId.SpeedyMeetingsTenant}
            when={triggerLightup}
        />
    );
}

function triggerLightup(lightup: () => void): void {
    lightup();
}
